import React, { useState } from "react";
import { Formik } from "formik";
import {
  Button,
  ButtonSet,
  Form,
  FormGroup,
  RadioButtonGroup,
  RadioButton,
  Select,
  SelectItem,
  TextInput,
} from "carbon-components-react";

import {
  makeEvent,
  booleanSelectItems,
  displayBoolValue,
  normalizeBoolEvent,
} from "web_modules/shared/FormHelpers/carbon-helpers";

import { getVALoanFormLabelFromKey } from "../../data-helpers";
import {
  validationSchema,
  VAPurposeOfLoanOptions,
  VAOccupancyOptions,
  VAVestingTypeOptions,
  VAVestingType,
} from "./form-values";

const styles = require("../form.module.scss");

interface Props {
  initialValues: any;
  onSubmit: (values) => void;
}

const VeteranAddendumForm: React.FC<Props> = ({ onSubmit, initialValues }: Props) => {
  const [editing, setEditing] = useState(false);

  return (
    <Formik
      enableReinitialize
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
      className={styles.form}
    >
      {({
        dirty,
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        resetForm,
        setValues,
        values,
      }) => {
        const {
          agency_case_number,
          purpose_of_loan,
          previous_va_home_loan,
          occupancy,
          was_aware_of_lower_valuation,
          vesting_type,
          vesting_type_other_description,
          funding_fee_financing,
        } = values;

        const sharedTextInputProps = (key: string) => ({
          readOnly: !editing,
          onChange: handleChange,
          onBlur: handleBlur,
          light: true,
          size: "sm",
          placeholder: "",
          labelText: getVALoanFormLabelFromKey(key),
        });

        const handleSelectChange = e => {
          handleChange(e);
          handleBlur(e); // reevaluate before organic blur, to enable "save" button immediately
        };

        const handleVestingTypeChange = event => {
          const diff = { ...values, vesting_type: event.target.value };

          // "Other Description" should only be set if the "Other" vesting_type is selected
          if (event.target.value !== VAVestingType.VAVestingTypeOther) {
            diff.vesting_type_other_description = "";
          }

          setValues(diff);
          handleBlur(event); // reevaluate before organic blur, to enable "save" button immediately
        };

        return (
          <Form
            onSubmit={e => {
              e.preventDefault();
              handleSubmit();
            }}
            className={styles.form}
          >
            <div className={styles.buttonContainer}>
              {!editing ? (
                <Button kind="secondary" size="sm" onClick={() => setEditing(true)}>
                  Edit
                </Button>
              ) : (
                <ButtonSet>
                  <Button
                    kind="tertiary"
                    size="sm"
                    onClick={() => {
                      setEditing(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button disabled={!dirty} kind="primary" size="sm" type="submit">
                    Save
                  </Button>
                </ButtonSet>
              )}
              <div style={{ marginBottom: "20px" }} />
            </div>
            <FormGroup legendText="Form 1802" className={styles.formInput}>
              <TextInput
                id="agency-case-number"
                name="agency_case_number"
                value={agency_case_number}
                {...sharedTextInputProps("agency_case_number")}
              />
              <Select
                id="purpose-of-loan"
                name="purpose_of_loan"
                value={purpose_of_loan}
                labelText={getVALoanFormLabelFromKey("purpose_of_loan")}
                onBlur={handleBlur}
                onChange={handleSelectChange}
                size="sm"
                disabled={!editing}
              >
                <SelectItem key="no-purpose-of-loan" text="Select a 'Purpose of Loan'" value="" />
                {VAPurposeOfLoanOptions}
              </Select>
              <Select
                id="previous-va-home-loan"
                name="previous_va_home_loan"
                value={displayBoolValue(previous_va_home_loan)}
                labelText={getVALoanFormLabelFromKey("previous_va_home_loan")}
                onChange={e => {
                  const event = makeEvent(
                    "previous_va_home_loan",
                    normalizeBoolEvent(e.target.value),
                  );
                  handleSelectChange(event);
                }}
                disabled={!editing}
              >
                <SelectItem key="previous-va-home-loan-null" text="Select..." value="null" />
                {booleanSelectItems}
              </Select>
              <Select
                id="occupancy"
                name="occupancy"
                value={occupancy}
                labelText={getVALoanFormLabelFromKey("occupancy")}
                onBlur={handleBlur}
                onChange={handleSelectChange}
                size="sm"
                disabled={!editing}
              >
                <SelectItem key="no-occupancy" text="Select an 'Occupancy'" value="" />
                {VAOccupancyOptions}
              </Select>
              <Select
                id="was-aware-of-lower-valuation"
                name="was_aware_of_lower_valuation"
                value={displayBoolValue(was_aware_of_lower_valuation)}
                labelText={getVALoanFormLabelFromKey("was_aware_of_lower_valuation")}
                onChange={e => {
                  const event = makeEvent(
                    "was_aware_of_lower_valuation",
                    normalizeBoolEvent(e.target.value),
                  );
                  handleSelectChange(event);
                }}
                disabled={!editing}
              >
                <SelectItem key="was-aware-of-lower-valuation-null" text="Select..." value="null" />
                {booleanSelectItems}
              </Select>
              <Select
                id="funding_fee_financing"
                name="funding_fee_financing"
                value={displayBoolValue(funding_fee_financing)}
                labelText={getVALoanFormLabelFromKey("funding_fee_financing")}
                onChange={e => {
                  const event = makeEvent(
                    "funding_fee_financing",
                    normalizeBoolEvent(e.target.value),
                  );
                  handleSelectChange(event);
                }}
                disabled={!editing}
              >
                <SelectItem key="funding-fee-financing" text="Select..." value="null" />
                {booleanSelectItems}
              </Select>
              <Select
                id="vesting_type"
                name="vesting_type"
                value={vesting_type}
                labelText={getVALoanFormLabelFromKey("vesting_type")}
                onChange={handleVestingTypeChange}
                disabled={!editing}
              >
                <SelectItem key="va-vesting-type-option-select" text="Select..." value="" />
                {VAVestingTypeOptions}
              </Select>
              {vesting_type === "Other" && (
                <TextInput
                  id="vesting-type-other-description"
                  name="vesting_type_other_description"
                  invalid={!!errors.vesting_type_other_description}
                  invalidText={errors.vesting_type_other_description}
                  value={vesting_type_other_description}
                  {...sharedTextInputProps("vesting_type_other_description")}
                />
              )}
            </FormGroup>
          </Form>
        );
      }}
    </Formik>
  );
};

export default VeteranAddendumForm;
